import React, { Component } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { FaPlus } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import { listItemMsg } from '@/shared/components/FormattedListValue';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { CanCreate, showWhenPermission } from '@/shared/components/security';
import handleHttpError from '@/shared/services/HandleHttpError';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import { injectStateService, stateServicePropType } from '@/shared/services/StateService';
import api from '../../../shared/api';
import config from '../../../config';
import ToastrService from '../../../shared/services/ToastrService';

import WorkflowConfigurationTable from './WorkflowConfigurationTable';
import WorkflowConfigurationModal from './WorkflowConfigurationModal';

import messages from './workflowConfigurationMessages';

export const EMAIL_ACTION_WORKFLOW_TYPE = 161;

// Survives remounts caused by translation reload (withIntl key change)
let persistedActiveTab = 0;

class WorkflowConfigurationContainer extends Component {
  state = {
    configurations: [],
    configurationModal: null,
    saving: false,
    showWorkflowConfigurationModal: false,
    isEmailAction: false,
    activeTab: persistedActiveTab,
  };

  setActiveTab = tab => {
    persistedActiveTab = tab;
    this.setState({ activeTab: tab });
  };

  componentDidMount() {
    const { intl, stateService } = this.props;

    if (stateService?.$state?.params?.tab === 'actions') {
      this.setActiveTab(1);
    }

    const workflowConfigurationEndpoint = `${config.appUrls.api}/api/workflowConfiguration`;
    const taskTemplatesPromise = api.base.get(
      `${config.appUrls.tasks}/api/TaskTemplates/Templates`,
    );

    Promise.all([api.base.get(workflowConfigurationEndpoint), taskTemplatesPromise]).then(
      ([{ data: configurations }, { data: taskTemplates }]) => {
        const translatedConfigurations = configurations
          .map(x => ({
            ...x,
            label: intl.formatMessage(listItemMsg('workflowtype', x.workflowType)),
          }))
          .sort(dynamicSort('label'));
        let translatedTemplates = {};
        taskTemplates.map(t => {
          const mainTaskTranslation = {
            [t.id]: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)),
          };
          translatedTemplates = {
            ...translatedTemplates,
            ...mainTaskTranslation,
          };
          if (t.children && t.children.length) {
            t.children.map(x => {
              const subtaskTranslation = {
                [x.id]: intl.formatMessage(listItemMsg('taskTemplate', x.id, x.name)),
              };
              translatedTemplates = {
                ...translatedTemplates,
                ...subtaskTranslation,
              };
            });
          }
        });
        this.setState(state => ({
          ...state,
          configurations: translatedConfigurations,
          translatedTemplates,
        }));
      },
    );
  }

  openEditDialog = (workflowConfiguration, isEmailAction) => {
    const { saving } = this.state;
    if (saving) return;

    this.setState({
      configurationModal: workflowConfiguration,
      showWorkflowConfigurationModal: true,
      isEmailAction:
        isEmailAction !== undefined
          ? isEmailAction
          : workflowConfiguration?.workflowType === EMAIL_ACTION_WORKFLOW_TYPE,
    });
  };

  handleError = error => {
    const { intl } = this.props;
    handleHttpError(intl)(error);

    this.setState({
      saving: false,
    });
  };

  saveConfiguration = configuration => {
    const { configurations } = this.state;
    if (configuration) {
      const existingConfiguration = configurations.find(o => o.id === configuration.id);
      if (existingConfiguration === undefined) {
        // Add
        this.setState(state => ({
          ...state,
          configurations: [
            {
              ...configuration,
              isNew: true,
            },
            ...state.configurations,
          ],
        }));
      } else {
        // Edit
        this.setState(state => ({
          ...state,
          configurations: state.configurations.map(s => {
            if (s.id === configuration.id) {
              return configuration;
            }
            return s;
          }),
        }));
      }
    }

    this.setState(state => ({
      ...state,
      showWorkflowConfigurationModal: false,
    }));
  };

  deleteConfiguration = configuration => {
    const { saving } = this.state;
    if (saving) return;

    const endpoint = `${config.appUrls.api}/api/workflowConfiguration/Delete/${configuration.id}`;
    this.setState({ saving: true }, () => {
      api.base
        .delete(endpoint)
        .then(() => {
          this.setState(state => ({
            ...state,
            saving: false,
            configurations: state.configurations.filter(s => s.id !== configuration.id),
          }));

          const { intl } = this.props;
          ToastrService.changesSaved(intl);
        })
        .catch(this.handleError);
    });
  };

  render() {
    const {
      configurations,
      configurationModal,
      showWorkflowConfigurationModal,
      translatedTemplates,
      isEmailAction,
      activeTab,
    } = this.state;
    const { intl } = this.props;

    const documentConfigurations = configurations.filter(
      c => c.workflowType !== EMAIL_ACTION_WORKFLOW_TYPE,
    );
    const actionConfigurations = configurations.filter(
      c => c.workflowType === EMAIL_ACTION_WORKFLOW_TYPE,
    );

    return (
      <>
        <ul className="nav nav-tabs nav-tabs-heading" role="tablist">
          <li
            role="presentation"
            className={classNames('tab', { active: activeTab === 0 })}
            onClick={() => this.setActiveTab(0)}
          >
            <span>
              <FormattedMessage {...messages.DocumentWorkflows} />
              <ZendeskHelp article="5434631627794" />
            </span>
          </li>
          <li
            role="presentation"
            className={classNames('tab', { active: activeTab === 1 })}
            onClick={() => this.setActiveTab(1)}
          >
            <span>
              <FormattedMessage {...messages.ActionWorkflows} />
              <ZendeskHelp article="26195811637404" />
            </span>
          </li>
        </ul>
        <div className="info-block-tab-panel panel">
          <div className="panel-heading">
            <CanCreate on="Settings">
              <div className="panel-buttons">
                <button
                  type="button"
                  className="btn btn-inverse"
                  onClick={() => this.openEditDialog(null, activeTab === 1)}
                >
                  <i className="fa fa-plus" />
                  <FormattedMessage {...messages.Add} />
                </button>
              </div>
            </CanCreate>
          </div>
          <WorkflowConfigurationTable
            configurations={activeTab === 0 ? documentConfigurations : actionConfigurations}
            taskTemplates={translatedTemplates}
            onDelete={this.deleteConfiguration}
            onEdit={this.openEditDialog}
            intl={intl}
            isEmailAction={activeTab === 1}
            deleting={this.state.saving}
          />
        </div>

        {showWorkflowConfigurationModal && (
          <WorkflowConfigurationModal
            show
            workflowConfiguration={configurationModal}
            isEmailAction={isEmailAction}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showWorkflowConfigurationModal: false,
              }))
            }
            onSave={this.saveConfiguration}
          />
        )}
      </>
    );
  }
}

WorkflowConfigurationContainer.propTypes = {
  intl: intlShape.isRequired,
  stateService: stateServicePropType,
};

export default showWhenPermission(
  'settings',
  'read',
)(injectStateService(injectIntl(WorkflowConfigurationContainer)));
